
import React, { useState, useEffect } from 'react';
import { MOCK_CATEGORIES } from '../lib/mockDb';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Loader2, 
  X, 
  Sparkles, 
  Image as ImageIcon, 
  Video, 
  Film,
  Play,
  ExternalLink,
  Info,
  Layers,
  FileJson,
  CheckCircle,
  Copy,
  ShoppingBag,
  Globe,
  Settings,
  ChevronDown,
  Trash,
  AlertTriangle
} from 'lucide-react';
import { getProductSmartDescription, generateSeoMeta } from '../services/geminiService';
import { generateProductVideo } from '../services/videoService';
import { productService } from '../services/productService';
import { Product, SellingModel, ProductAttribute, ProductVariant, Store } from '../types';
import { storage } from '../lib/storage';
import { Language, translations } from '../lib/i18n';
import { billingService } from '../services/billingService';

interface AdminProductsProps {
  storeId: string;
  lang: Language;
}

const AdminProducts = ({ storeId, lang }: AdminProductsProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [store, setStore] = useState<Store>(storage.getStoreConfig(storeId));
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSeoGenerating, setIsSeoGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const t = translations[lang];

  // Variations State
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  // Multi-Image State
  const [imageUrls, setImageUrls] = useState<string[]>(['']);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    categoryId: MOCK_CATEGORIES[0].id,
    sellingModel: SellingModel.INTERNAL as SellingModel,
    externalUrl: '',
    affiliatePlatform: 'Amazon'
  });

  const [seoData, setSeoData] = useState<{ title: string, keywords: string[], metaDescription: string } | null>(null);

  useEffect(() => {
    loadProducts();
    setStore(storage.getStoreConfig(storeId));
  }, [storeId]);

  const loadProducts = async () => {
    setIsLoading(true);
    const data = await productService.getStoreProducts(storeId);
    setProducts(data);
    setIsLoading(false);
  };

  const handleOpenModal = () => {
    const canAdd = billingService.checkLimit(storeId, 'product');
    const subStatus = store.subscription?.status;
    
    if (!canAdd) {
      return alert("Product limit reach ho chuki hai bhai! Please upgrade your plan.");
    }
    if (subStatus === 'OVERDUE') {
      return alert("Aapka subscription payment pending hai. Pehle billing clear karein!");
    }
    
    setEditingProduct(null);
    setFormData({ name: '', description: '', price: '', stock: '', categoryId: MOCK_CATEGORIES[0].id, sellingModel: SellingModel.INTERNAL, externalUrl: '', affiliatePlatform: 'Amazon' });
    setImageUrls(['']);
    setAttributes([]);
    setVariants([]);
    setIsModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      stock: product.stock.toString(),
      categoryId: product.categoryId,
      sellingModel: product.sellingModel || SellingModel.INTERNAL,
      externalUrl: product.externalUrl || '',
      affiliatePlatform: product.affiliatePlatform || 'Amazon'
    });
    setImageUrls(product.images && product.images.length > 0 ? product.images : ['']);
    setAttributes(product.attributes || []);
    setVariants(product.variants || []);
    setSeoData(null);
    setIsModalOpen(true);
  };

  const handleAddImageUrl = () => {
    setImageUrls([...imageUrls, '']);
  };

  const handleRemoveImageUrl = (index: number) => {
    if (imageUrls.length > 1) {
      setImageUrls(imageUrls.filter((_, i) => i !== index));
    } else {
      setImageUrls(['']);
    }
  };

  const handleImageUrlChange = (index: number, value: string) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const addAttribute = () => {
    setAttributes([...attributes, { name: '', options: [''] }]);
  };

  const updateAttributeName = (idx: number, name: string) => {
    const next = [...attributes];
    next[idx].name = name;
    setAttributes(next);
  };

  const addAttributeOption = (idx: number) => {
    const next = [...attributes];
    next[idx].options.push('');
    setAttributes(next);
  };

  const updateAttributeOption = (attrIdx: number, optIdx: number, val: string) => {
    const next = [...attributes];
    next[attrIdx].options[optIdx] = val;
    setAttributes(next);
  };

  const removeAttribute = (idx: number) => {
    setAttributes(attributes.filter((_, i) => i !== idx));
  };

  const generateVariants = () => {
    if (attributes.length === 0) return;
    
    const generate = (sets: string[][], prefix: string[] = []): string[][] => {
      if (sets.length === 0) return [prefix];
      const result: string[][] = [];
      const currentSet = sets[0];
      const remainingSets = sets.slice(1);
      for (const val of currentSet) {
        if (val.trim()) {
          result.push(...generate(remainingSets, [...prefix, val]));
        }
      }
      return result;
    };

    const combinations = generate(attributes.map(a => a.options));
    
    const newVariants: ProductVariant[] = combinations.map((combo, idx) => {
      const name = combo.join(' / ');
      const attrMap: Record<string, string> = {};
      combo.forEach((val, i) => {
        attrMap[attributes[i].name] = val;
      });

      return {
        id: `v-${Date.now()}-${idx}`,
        name,
        price: Number(formData.price) || 0,
        stock: Number(formData.stock) || 0,
        attributes: attrMap
      };
    });

    setVariants(newVariants);
  };

  const updateVariantPrice = (idx: number, val: string) => {
    const next = [...variants];
    next[idx].price = Number(val);
    setVariants(next);
  };

  const updateVariantStock = (idx: number, val: string) => {
    const next = [...variants];
    next[idx].stock = Number(val);
    setVariants(next);
  };

  const handleAiDescribe = async () => {
    if (!formData.name) return alert("Enter name first!");
    setIsGenerating(true);
    const desc = await getProductSmartDescription(formData.name);
    setIsGenerating(false);
    if (desc) setFormData(prev => ({ ...prev, description: desc }));
  };

  const handleGenerateSeo = async () => {
    if (!formData.name || !formData.description) return alert("Enter name and description first!");
    setIsSeoGenerating(true);
    const seo = await generateSeoMeta(formData.name, formData.description);
    setIsSeoGenerating(false);
    if (seo) setSeoData(seo);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    const validImages = imageUrls.filter(url => url.trim() !== '');
    const productImages = validImages.length > 0 ? validImages : ['https://picsum.photos/seed/gadget/600/600'];

    const productData: Omit<Product, 'id'> = {
      name: formData.name,
      description: formData.description,
      price: Number(formData.price),
      stock: formData.sellingModel === SellingModel.AFFILIATE ? 999 : Number(formData.stock),
      categoryId: formData.categoryId,
      storeId: storeId,
      images: productImages,
      sellingModel: formData.sellingModel,
      externalUrl: formData.sellingModel === SellingModel.AFFILIATE ? formData.externalUrl : undefined,
      affiliatePlatform: formData.sellingModel === SellingModel.AFFILIATE ? formData.affiliatePlatform : undefined,
      attributes: attributes.length > 0 ? attributes : undefined,
      variants: variants.length > 0 ? variants : undefined,
      createdAt: editingProduct?.createdAt || new Date().toISOString()
    };

    if (editingProduct) {
      const all = storage.getProducts();
      const updated = all.map(p => p.id === editingProduct.id ? { ...productData, id: p.id } as Product : p);
      storage.saveProducts(updated);
      setProducts(updated.filter(p => p.storeId === storeId));
    } else {
      await productService.addProduct(productData);
      loadProducts();
    }

    setIsSaving(false);
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">{t.products}</h1>
          <div className="flex items-center gap-2">
            <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">{products.length} Catalog Items</p>
            {store.subscription?.status === 'OVERDUE' && (
              <span className="flex items-center gap-1 text-[8px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full uppercase">
                <AlertTriangle className="h-2 w-2" /> Billing Locked
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-4">
          <button 
            onClick={handleOpenModal} 
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl font-black shadow-xl shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all"
          >
            <Plus className="h-5 w-5" /> Add Product
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[40px] border border-gray-100 overflow-hidden shadow-xl shadow-gray-100/50">
        <table className="w-full text-left">
          <thead className="bg-gray-50/50 text-[10px] uppercase font-black text-gray-400 tracking-widest border-b border-gray-50">
            <tr>
              <th className="px-8 py-5">Gadget</th>
              <th className="px-8 py-5">Selling Model</th>
              <th className="px-8 py-5">Base Price</th>
              <th className="px-8 py-5">Variations</th>
              <th className="px-8 py-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {products.map(p => (
              <tr key={p.id} className="group hover:bg-gray-50/50 transition-colors">
                <td className="px-8 py-6">
                  <div className="flex items-center gap-4">
                    <img src={p.images[0]} className="w-12 h-12 rounded-xl object-cover border border-gray-100" alt="" />
                    <span className="font-black text-sm text-gray-900">{p.name}</span>
                  </div>
                </td>
                <td className="px-8 py-6">
                  {p.sellingModel === SellingModel.AFFILIATE ? (
                    <span className="flex items-center gap-1.5 text-[9px] font-black text-amber-600 bg-amber-50 px-2 py-1 rounded-lg uppercase tracking-widest border border-amber-100">
                      <ExternalLink className="h-3 w-3" /> Affiliate
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg uppercase tracking-widest border border-blue-100">
                      <ShoppingBag className="h-3 w-3" /> Internal
                    </span>
                  )}
                </td>
                <td className="px-8 py-6 font-black text-gray-900 text-lg">${p.price.toLocaleString()}</td>
                <td className="px-8 py-6">
                   {p.variants && p.variants.length > 0 ? (
                     <span className="flex items-center gap-1.5 text-[9px] font-black text-purple-600 bg-purple-50 px-2 py-1 rounded-lg uppercase tracking-widest border border-purple-100">
                       <Settings className="h-3 w-3" /> {p.variants.length} Variants
                     </span>
                   ) : (
                     <span className="text-[10px] font-bold text-gray-400">Single Edition</span>
                   )}
                </td>
                <td className="px-8 py-6 text-right">
                   <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleEdit(p)} className="p-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => productService.deleteProduct(p.id).then(loadProducts)} className="p-3 text-red-600 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="h-4 w-4" /></button>
                   </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-3xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-2xl font-black text-gray-900 tracking-tight">{editingProduct ? 'Update Gadget' : 'New Gadget'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-8 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-8">
                {/* Basic Details */}
                <div className="col-span-2 space-y-6">
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Product Name</label>
                      <input required placeholder="Lynq Pro Max" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold" />
                   </div>
                   
                   <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Base Price ($)</label>
                        <input required type="number" placeholder="999" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Base Stock</label>
                        <input required type="number" placeholder="50" value={formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})} className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold" />
                      </div>
                   </div>
                </div>

                {/* Multi-Image Section */}
                <div className="col-span-2 space-y-4 p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100">
                   <div className="flex justify-between items-center">
                      <h4 className="text-lg font-black text-gray-900 flex items-center gap-2">
                         <ImageIcon className="h-5 w-5 text-blue-600" /> Product Gallery
                      </h4>
                      <button type="button" onClick={handleAddImageUrl} className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-sm">
                         <Plus className="h-3 w-3" /> Add Image
                      </button>
                   </div>
                   <div className="space-y-3">
                      {imageUrls.map((url, idx) => (
                        <div key={idx} className="flex gap-3">
                           <div className="flex-grow relative">
                              <input 
                                placeholder="https://..." 
                                value={url} 
                                onChange={e => handleImageUrlChange(idx, e.target.value)} 
                                className="w-full px-5 py-4 bg-white border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold text-sm" 
                              />
                           </div>
                           <button type="button" onClick={() => handleRemoveImageUrl(idx)} className="p-4 bg-white text-red-400 hover:text-red-600 rounded-2xl shadow-sm"><Trash2 className="h-5 w-5" /></button>
                        </div>
                      ))}
                   </div>
                   <div className="flex flex-wrap gap-4 mt-4">
                      {imageUrls.filter(u => u.trim()).map((url, idx) => (
                        <div key={idx} className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white shadow-md">
                           <img src={url} className="w-full h-full object-cover" alt="" onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/150?text=Invalid')} />
                        </div>
                      ))}
                   </div>
                </div>

                {/* Variants Section */}
                <div className="col-span-2 space-y-6 p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100">
                   <div className="flex justify-between items-center">
                      <h4 className="text-lg font-black text-gray-900 flex items-center gap-2">
                         <Settings className="h-5 w-5 text-blue-600" /> Product Variations
                      </h4>
                      <button type="button" onClick={addAttribute} className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-sm">
                         <Plus className="h-3 w-3" /> Add Option
                      </button>
                   </div>

                   <div className="space-y-6">
                      {attributes.map((attr, attrIdx) => (
                        <div key={attrIdx} className="bg-white p-6 rounded-3xl border border-gray-200 space-y-4">
                           <div className="flex justify-between items-center">
                              <input 
                                placeholder="Attribute Name (e.g., Color)" 
                                value={attr.name} 
                                onChange={(e) => updateAttributeName(attrIdx, e.target.value)}
                                className="text-sm font-black text-gray-900 border-none focus:ring-0 p-0 w-1/2" 
                              />
                              <button type="button" onClick={() => removeAttribute(attrIdx)} className="text-red-400 hover:text-red-600"><Trash className="h-4 w-4" /></button>
                           </div>
                           <div className="flex flex-wrap gap-2">
                              {attr.options.map((opt, optIdx) => (
                                <div key={optIdx} className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-xl">
                                   <input 
                                     placeholder="Value" 
                                     value={opt} 
                                     onChange={(e) => updateAttributeOption(attrIdx, optIdx, e.target.value)}
                                     className="bg-transparent border-none text-xs font-bold w-20 focus:ring-0" 
                                   />
                                </div>
                              ))}
                              <button type="button" onClick={() => addAttributeOption(attrIdx)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-all">
                                 <Plus className="h-4 w-4" />
                              </button>
                           </div>
                        </div>
                      ))}
                   </div>

                   {attributes.length > 0 && (
                     <button type="button" onClick={generateVariants} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-100">
                        Generate All Variant Combinations
                     </button>
                   )}

                   {variants.length > 0 && (
                     <div className="space-y-3 pt-4">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Variant Specifics</label>
                        <div className="space-y-2">
                           {variants.map((v, vIdx) => (
                             <div key={vIdx} className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-100">
                                <span className="text-xs font-black text-gray-900 flex-grow">{v.name}</span>
                                <div className="flex gap-2">
                                   <input 
                                     type="number" 
                                     placeholder="Price" 
                                     value={v.price} 
                                     onChange={(e) => updateVariantPrice(vIdx, e.target.value)}
                                     className="w-24 bg-gray-50 border-none rounded-xl text-xs font-bold p-2" 
                                   />
                                   <input 
                                     type="number" 
                                     placeholder="Stock" 
                                     value={v.stock} 
                                     onChange={(e) => updateVariantStock(vIdx, e.target.value)}
                                     className="w-20 bg-gray-50 border-none rounded-xl text-xs font-bold p-2" 
                                   />
                                </div>
                             </div>
                           ))}
                        </div>
                     </div>
                   )}
                </div>

                <div className="col-span-2 space-y-2 relative">
                   <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</label>
                   <textarea required placeholder="Explain why this gadget is amazing..." rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full px-5 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-500 font-bold resize-none" />
                   <button type="button" onClick={handleAiDescribe} className="absolute right-3 bottom-3 text-white font-black text-[10px] flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-2 rounded-xl shadow-lg shadow-purple-200">
                      {isGenerating ? <Loader2 className="animate-spin h-3 w-3" /> : <Sparkles className="h-3 w-3" />} AI DESCRIBE
                   </button>
                </div>
              </div>
              <button disabled={isSaving} className="w-full bg-blue-600 text-white py-6 rounded-[2rem] font-black text-lg shadow-2xl shadow-blue-100 hover:bg-blue-700 active:scale-[0.98] transition-all">
                {isSaving ? 'Deploying...' : 'Save & Publish Product'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
